import React, { useContext, useState, useRef } from "react";
import intl from "react-intl-universal";
import { mdiClose, mdiCalculator } from "@mdi/js";
import commonUtils from "../../../script/utils/commonUtils";

import calculationRest from "../../../rest/calculationRest";
import calcSettingUtils from "../../../script/utils/calcSettingUtils";

import { useInput } from "../../hooks/useInput";
import { useOnChange } from "../../hooks/useOnChange";
import { EntryContext } from "../../contexts/EntryContext";
import { ValidationContext } from "../../contexts/ValidationContext";

import { Grid } from "../../components/Grid";
import { Dialog } from "../../containers/Dialog";
import { Button } from "../../components/Button";
import { Spacer } from "../../components/Spacer";
import { Checkbox } from "../../components/Checkbox";
import { DialogBody } from "../../containers/DialogBody";
import { useValidation } from "../../hooks/useValidation";
import { InputNumber } from "../../components/InputNumber";
import { DialogFooter } from "../../containers/DialogFooter";
import { SelectSingle } from "../../components/SelectSingle";
import { SelectMultiple } from "../../components/SelectMultiple";

export const WattwaechterDialog = props => {

    const entryContext = useContext(EntryContext);
    const validationContext = useContext(ValidationContext);
    const { entry } = entryContext;

    const getCalcSetting = () => {
        if(entry.calcSetting === undefined || entry.calcSetting === null) {
            return calcSettingUtils.empty();
        }
        return calcSettingUtils.clone(entry.calcSetting);
    };

    const [calcSetting, setCalcSetting] = useState(getCalcSetting());

    const calcAreasRef = useRef([]);
    const calcAreas = useInput("calcAreas", calcAreasRef.current);

    const fastCalc = useInput("fastCalc", calcSetting.fastCalc);
    const sliceNum = useInput("sliceNum", calcSetting.sliceNum);
    const sliceDist = useInput("sliceDist", calcSetting.sliceDist);
    const fieldType = useInput("fieldType", calcSetting.fieldType);
    const resolution = useInput("resolution", calcSetting.resolution);
    const preferFarField = useInput("preferFarField", calcSetting.preferFarField);
    const environmentFactor = useInput("environmentFactor", calcSetting.environmentFactor);

    useValidation(value => !commonUtils.isBlank(value), [sliceDist]);
    useValidation(value => !commonUtils.isBlank(value), [environmentFactor]);

    useValidation((sliceNum, resolution) => {
        if (commonUtils.isBlank(resolution)) {
            return false;
        }
        const maxSlicesForResolution = new Map([
            [1000, 5],
            [875, 25],
        ]);
        const num = Number(sliceNum.replace(",", "."));
        const res = Number(resolution);
        const max = maxSlicesForResolution.get(res) ?? 80;
        if (max && num > max) {
            return intl.get("entry.wattwaechterSetting.error.sliceNum.maxSlices", { resolution: resolution, max });
        }
        return true;
    }, [sliceNum, resolution]);

    useOnChange((name, value) => {
        calcSetting[name] = value;
        setCalcSetting(calcSetting);
    }, [fieldType, sliceNum, sliceDist, fastCalc,
        preferFarField, environmentFactor, resolution]);

    const onWattwaechter = async () => {
        const allValid = await validationContext.allValid();
        validationContext.setErrorsVisible(true);
        const { entry } = entryContext;
        entry.calcSetting = calcSetting;
        if (allValid) {
            if (props.saveEntry) {
                await props.saveEntry(entry);
            }
            const calcAreaIds = calcAreas.value.map(area => area.id);
            const updatedEntry = await calculationRest.wattwaechter(entry.id, calcAreaIds);
            entryContext.setEntry(updatedEntry);
            props.onClose();
        }
    };

    return (
        <Dialog
            width="430px"
            title={intl.get("entry.wattwaechter")}
            onClose={() => props.onClose()}>
            <DialogBody>
                <Grid columns="1">
                    <SelectMultiple {...calcAreas.bind}
                        title={intl.get("entry.wattwaechterSetting.areas")}
                        disabled={entry.calcSetting.calcAreas.length <= 0}
                        toLabel={value => value.name + (value.height ? ` ${value.height}m` : "")}>
                        {entry.calcSetting.calcAreas
                            .filter(ca => ca.name !== "OVERVIEW")
                            .map(ca => (
                                <option key={ca.id} value={ca}>{ca.name + (ca.height ? ` ${ca.height}m` : "")}</option>
                            ))
                        }
                    </SelectMultiple>
                    <SelectSingle {...fieldType.bind}
                        title={intl.get("entry.wattwaechterSetting.fieldType")}
                        toLabel={value => intl.get("enum.fieldType." + value)}>
                        <option value="E_FIELD" >{intl.get("enum.fieldType.E_FIELD")}</option>
                        <option value="H_FIELD">{intl.get("enum.fieldType.H_FIELD")}</option>
                        <option value="PROTECTION_AREA_BLMSCHV">{intl.get("enum.fieldType.PROTECTION_AREA_BLMSCHV")}</option>
                        <option value="PROTECTION_AREA_EXP_1">{intl.get("enum.fieldType.PROTECTION_AREA_EXP_1")}</option>
                        <option value="EXHAUSTION_FACTOR_BLMSCHV">{intl.get("enum.fieldType.EXHAUSTION_FACTOR_BLMSCHV")}</option>
                        <option value="EXHAUSTION_FACTOR_EXP_1">{intl.get("enum.fieldType.EXHAUSTION_FACTOR_EXP_1")}</option>
                    </SelectSingle>
                    <SelectSingle {...resolution.bind}
                        title={intl.get("entry.wattwaechterSetting.resolution")}>
                        <option value="1000">1000</option>
                        <option value="875">875</option>
                        <option value="750">750</option>
                        <option value="625">625</option>
                        <option value="500">500</option>
                        <option value="375">375</option>
                        <option value="250">250</option>
                        <option value="125">125</option>
                        <option value="0">0</option>
                    </SelectSingle>
                    <Grid columns="2">
                        <InputNumber {...sliceNum.bind} title={intl.get("entry.wattwaechterSetting.sliceNum")} />
                        <InputNumber {...sliceDist.bind} title={intl.get("entry.wattwaechterSetting.sliceDist")} fractionDigits={2} />
                    </Grid>
                    <Grid columns="2">
                        <Checkbox {...preferFarField.bind} title={intl.get("entry.wattwaechterSetting.preferFarField")} />
                        <Checkbox {...fastCalc.bind} title={intl.get("entry.wattwaechterSetting.fastCalc")} />
                    </Grid>
                    <InputNumber {...environmentFactor.bind}
                        fractionDigits={4}
                        title={intl.get("entry.wattwaechterSetting.environmentFactor")} />
                </Grid>
            </DialogBody>
            <DialogFooter>
                <Button
                    svgIcon={mdiClose}
                    onClick={() => props.onClose()}
                    value={intl.get("common.cancel")} />
                <Spacer variant="vertical" />
                <Button
                    disabled={calcAreas.value.length <= 0}
                    svgIcon={mdiCalculator}
                    onClick={onWattwaechter}
                    value={intl.get("entry.calculate")} />
            </DialogFooter>
        </Dialog>
    );
};
